/**
 * Canonical telemetry fields (server: `telemetry/canonical.py` FIELDS and LABELS): the values a
 * device's profile maps its messages to — `ext_voltage`, `temp_1`, `rpm` … Screens show the
 * field's label and unit, never its key alone: a rule condition reads "Supply voltage < 11 V",
 * the device console "Engine speed 1 200 rpm".
 *
 * The server sends each field's English label and unit with the data that names it (the asset
 * rule catalogue, the device console). The labels are marked here so the nl and fr catalogues
 * translate them; a field the server adds later shows its English label until it is added here.
 */
import { formatNumber } from './format';
import { msg, t } from './i18n';

export interface FieldInfo {
  key: string;
  label: string;
  unit?: string | null;
  type?: string;
  description?: string;
}

/** key → label and unit, as a screen received it from the server. */
export type FieldLookup = (key: string) => FieldInfo | undefined;

export const fieldLookup = (fields: readonly FieldInfo[] | undefined): FieldLookup => {
  const byKey = new Map((fields ?? []).map((f) => [f.key, f]));
  return (key) => byKey.get(key);
};

/** The field's label in the reader's language, or its key when the server did not describe it. */
export function fieldLabel(key: string, lookup?: FieldLookup): string {
  const info = lookup?.(key);
  return info ? t(info.label) : key;
}

/** A value with its unit ("11 V", "1 200 rpm", "on"); booleans read as on/off. */
export function fieldValue(value: unknown, info?: FieldInfo): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? t('on') : t('off');
  if (typeof value === 'number') {
    // Coordinates keep metre precision; other measurements two decimals at most.
    const digits = info?.unit === 'deg' ? 5 : Math.abs(value) < 10 ? 2 : 1;
    const n = Number.isInteger(value) ? formatNumber(value) : formatNumber(value, digits);
    return info?.unit ? `${n} ${info.unit}` : n;
  }
  if (typeof value === 'string') return info?.unit && value.trim() !== '' && !Number.isNaN(Number(value)) ? `${value} ${info.unit}` : value;
  return JSON.stringify(value);
}

/** The server's labels (canonical.py LABELS), marked for translation. */
export const FIELD_LABELS = [
  msg('Latitude'),
  msg('Longitude'),
  msg('Speed (GPS)'),
  msg('Heading'),
  msg('Altitude'),
  msg('Satellites'),
  msg('GPS precision (HDOP)'),
  msg('Ignition'),
  msg('Movement'),
  msg('Supply voltage'),
  msg('Device battery'),
  msg('Odometer'),
  msg('GPS odometer'),
  msg('Speed (CAN)'),
  msg('Engine speed'),
  msg('Engine load'),
  msg('Accelerator pedal'),
  msg('Brake pedal'),
  msg('Cruise control'),
  msg('PTO'),
  msg('Fuel used (counter)'),
  msg('Fuel rate'),
  msg('Fuel level'),
  msg('Fuel in tank'),
  msg('Energy used (counter)'),
  msg('State of charge'),
  msg('Acceleration X'),
  msg('Acceleration Y'),
  msg('Acceleration Z'),
  msg('Harsh event'),
  msg('Harsh event peak'),
  msg('Harsh event duration'),
  msg('Cornering yaw rate'),
  msg('Harsh event peak, longitudinal'),
  msg('Harsh event peak, lateral'),
  msg('Headway'),
  msg('Headway warning'),
  msg('Forward collision warning'),
  msg('Pedestrian collision warning'),
  msg('Lane departure warning'),
  msg('Distraction'),
  msg('Phone use'),
  msg('Yawning'),
  msg('Smoking'),
  msg('Eyes closed'),
  msg('Fatigue'),
  msg('Driver camera blocked'),
  msg('Driver card (slot 1)'),
  msg('Driver card (slot 2)'),
  msg('Tachograph activity (slot 1)'),
  msg('Tachograph activity (slot 2)'),
  msg('Tachograph overspeed'),
  msg('Driver key'),
  msg('Beacons'),
  msg('VIN'),
  msg('Temperature 1'),
  msg('Temperature 2'),
  msg('Digital input 1'),
  msg('Digital input 2'),
  msg('GSM signal'),
  msg('Speed limit'),
];
